import { useSetAtom } from "jotai";
import type { JSX } from "react";

import {
  type AnyRoute,
  Outlet,
  createRoute,
  useNavigate,
} from "@tanstack/react-router";

import { ClientEditorDialog } from "../ClientEditorDialog";
import { ClientList } from "../ClientList";
import { reloadClientListAtom } from "../stores/list";
import { refreshClientQuickviewAtom } from "../stores/quickview";
import type { ClientListFilter } from "../types";

const DRAFT = "new";

type Navigate = (options: {
  to: string;
  params?: Record<string, unknown>;
  replace?: boolean;
}) => Promise<void>;

export type ClientRoutesOptions = {
  filter?: ClientListFilter;
};

export function clientRoutes(
  parent: AnyRoute,
  { filter = "active" }: ClientRoutesOptions = {},
): AnyRoute {
  const section = createRoute({
    getParentRoute: () => parent,
    id: "clients",
    component: function ClientSectionRoute(): JSX.Element {
      const navigate = useNavigate() as unknown as Navigate;

      return (
        <>
          <ClientList
            filter={filter}
            onOpen={(clientId) =>
              void navigate({ to: editor.to, params: { clientId } })
            }
          />

          <Outlet />
        </>
      );
    },
  });

  const index = createRoute({
    getParentRoute: () => section,
    path: "/",
    component: () => null,
  });

  const editor = createRoute({
    getParentRoute: () => section,
    path: "$clientId",
    params: {
      parse: ({ clientId }) => ({
        clientId: DRAFT === clientId ? null : Number(clientId),
      }),
      stringify: ({ clientId }) => ({
        clientId: null === clientId ? DRAFT : String(clientId),
      }),
    },
    component: function ClientEditorRoute(): JSX.Element {
      const { clientId } = editor.useParams();
      const navigate = useNavigate() as unknown as Navigate;

      const reload = useSetAtom(reloadClientListAtom);
      const refreshQuickview = useSetAtom(refreshClientQuickviewAtom);

      const close = () => void navigate({ to: index.to, replace: true });

      return (
        <ClientEditorDialog
          key={clientId ?? DRAFT}
          open
          onOpenChange={(next) => {
            if (!next) {
              close();
            }
          }}
          clientId={clientId}
          onSaved={(savedId) => {
            close();
            void refreshQuickview(savedId);
            void reload();
          }}
          onCancel={close}
        />
      );
    },
  });

  return section.addChildren([index, editor]);
}
